import { useCallback, useMemo } from 'react';

import { isGroupHeader } from '../helpers';
import {
  areGroupedItems,
  flattenGroupItemsToDownshift,
  flattenGroupItemsToNonDownshift,
  getStartingIndices,
} from '../helpers/groupedItems';
import { FlatGroupedItem, GenericItem, GroupHeader, ItemsProp } from '../types';

export default <TItem extends GenericItem>({
  maybeGroupedItems,
}: {
  maybeGroupedItems: ItemsProp<TItem>;
}) => {
  const isGrouped = areGroupedItems(maybeGroupedItems);

  const flatItems = useMemo(
    () => flattenGroupItemsToDownshift(maybeGroupedItems),
    [maybeGroupedItems],
  );

  const { flatItemsWithGroupHeaders, mapDownshiftItemToItemWithGroupInfo } =
    useMemo(() => {
      if (!areGroupedItems(maybeGroupedItems)) {
        return {
          startingIndices: new Map<number, number>(),
          indexGetter: undefined,
        };
      }

      const startingIndices = getStartingIndices(maybeGroupedItems);
      const flatItemsWithGroupHeaders = flattenGroupItemsToNonDownshift(
        maybeGroupedItems,
        startingIndices,
      );
      const flatItemsWithGroupInfo = flatItemsWithGroupHeaders.filter(
        (item) => !isGroupHeader(item),
      ) as FlatGroupedItem<TItem>[]; // The filter removes the GroupHeaders but they're still included in the type

      const mapDownshiftItemToItemWithGroupInfo = flatItems.reduce(
        (map, curr, i) => {
          map.set(curr, flatItemsWithGroupInfo[i]);

          return map;
        },
        new Map<TItem, FlatGroupedItem<TItem>>(),
      );

      return {
        flatItemsWithGroupHeaders,
        mapDownshiftItemToItemWithGroupInfo,
      };
    }, [flatItems, maybeGroupedItems]);

  /**
   * Gets the index for downshift
   * We need to determine this manually so we skip over group headers
   */
  const indexGetter = useMemo(
    () =>
      isGrouped
        ? (item: TItem): number | undefined =>
            mapDownshiftItemToItemWithGroupInfo?.get(item)?.index
        : null,
    [isGrouped, mapDownshiftItemToItemWithGroupInfo],
  );

  /** Adds group headers for provided items
   * @param itemsToShow The (potentially) filtered list of items to show
   */
  const getMenuItems = useCallback(
    (itemsToShow: TItem[]) => {
      if (!isGrouped) {
        return itemsToShow;
      }

      const filteredGroupedMenuItems: (GroupHeader | TItem)[] = [];

      itemsToShow.forEach((filteredItem, i) => {
        const filteredGroupedItem =
          mapDownshiftItemToItemWithGroupInfo?.get(filteredItem);
        if (!filteredGroupedItem) {
          return;
        }

        const groupHeader = flatItemsWithGroupHeaders?.find(
          (item) =>
            item.isGroupHeader &&
            item.groupKey === filteredGroupedItem.groupKey,
        );

        if (groupHeader && !filteredGroupedMenuItems.includes(groupHeader)) {
          filteredGroupedMenuItems.push(groupHeader);
        }

        filteredGroupedItem.index = i;
        filteredGroupedMenuItems.push(filteredItem);
      });

      return filteredGroupedMenuItems;
    },
    [flatItemsWithGroupHeaders, isGrouped, mapDownshiftItemToItemWithGroupInfo],
  );

  return {
    flatItems,
    indexGetter,
    getMenuItems,
  };
};
